/****************
 * A new page starts at the top.
 *
 * React Router keeps the scroll position across a route change, so going from
 * a session you had scrolled halfway down to the dashboard dropped you into
 * the middle of the dashboard. Guillaume, 2026-09-03.
 *
 * It watches the PATHNAME only, not the search string: arriving from a
 * notification at /session/:id?order=:orderId is a deep link that wants to land
 * on the order, and that case is skipped outright so the two do not fight.
 ****************/

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollPageToTop } from '@/lib/scroll';
import { hasFocusParam } from '@/lib/focusLink.core';

export function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // A deep link is asking for a specific row (?order= / ?open= / ?trade=,
    // lib/focusLink.core.ts). Leave it alone: the page centres that row.
    if (hasFocusParam(search)) return;
    scrollPageToTop();
    /* Again while the new page settles: once after one frame was not enough
       (Guillaume 11-03: "sometime I change page and I'm still scrolled down"):
       a lazy page, its data or a control that scrolls itself into view lands
       later. Up to 600 ms, and never once the person scrolls by hand. */
    let stopped = false;
    const stop = () => { stopped = true; };
    const opts = { passive: true, capture: true } as const;
    window.addEventListener('wheel', stop, opts);
    window.addEventListener('touchmove', stop, opts);
    window.addEventListener('keydown', stop, opts);
    const again = () => { if (!stopped && !hasFocusParam(window.location.search)) scrollPageToTop(); };
    const raf = requestAnimationFrame(again);
    const timers = [80, 200, 400, 600].map((ms) => window.setTimeout(again, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('wheel', stop, opts);
      window.removeEventListener('touchmove', stop, opts);
      window.removeEventListener('keydown', stop, opts);
    };
    // `search` is read but intentionally not a dependency: changing a filter in
    // the query string is not a new page and must not throw you to the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}
